import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Assessment as AssessmentIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  History as HistoryIcon,
  Security as SecurityIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

// ============================================================================
// MY DATA PAGE
// GDPR Right to Access - View all personal data
// ============================================================================

interface UserData {
  user: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    username: string;
    date_joined: string;
    last_login: string | null;
    is_active: boolean;
  };
  companies: Array<{
    id: number;
    name: string;
    type: string;
    role: string;
    joined_at: string;
  }>;
  statistics: {
    audits_created: number;
    action_items_assigned: number;
    companies_member_of: number;
  };
  consents: Array<{
    type: string;
    consented: boolean;
    date: string;
  }>;
  recent_activity: Array<{
    action: string;
    resource: string;
    date: string;
  }>;
}

interface DeletionStatus {
  has_pending_request: boolean;
  requested_at?: string;
  scheduled_deletion_at?: string;
  days_remaining?: number;
  can_cancel?: boolean;
}

interface ExportStatus {
  exports: Array<{
    id: number;
    status: string;
    created_at: string;
    is_downloadable: boolean;
  }>;
}

const MyData: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  
  // State
  const [userData, setUserData] = useState<UserData | null>(null);
  const [deletionStatus, setDeletionStatus] = useState<DeletionStatus | null>(null);
  const [exportStatus, setExportStatus] = useState<ExportStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  
  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState<string>('');

  // Load all data on mount
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const [userDataRes, deletionRes, exportRes] = await Promise.all([
        api.get('/account/data/'),
        api.get('/account/delete/'),
        api.get('/account/export/'),
      ]);
      
      setUserData(userDataRes.data);
      setDeletionStatus(deletionRes.data);
      setExportStatus(exportRes.data);
    } catch (err: any) {
      console.error('Failed to load data:', err);
      setError('Failed to load your data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Request data export
  const handleRequestExport = async () => {
    setIsExporting(true);
    setExportMessage('');
    
    try {
      const response = await api.post('/account/export/');
      setExportMessage('Export started! You will receive an email when it\'s ready.');
      // Refresh export status
      const exportRes = await api.get('/account/export/');
      setExportStatus(exportRes.data);
    } catch (err: any) {
      if (err.response?.status === 429) {
        setExportMessage('You can only request one export per 24 hours.');
      } else {
        setExportMessage('Failed to start export. Please try again.');
      }
    } finally {
      setIsExporting(false);
    }
  };

  // Request account deletion
  const handleRequestDeletion = async () => {
    if (deleteConfirmText !== 'DELETE MY ACCOUNT') {
      setError('Please type "DELETE MY ACCOUNT" exactly to confirm');
      return;
    }
    
    setIsDeleting(true);
    setError('');
    
    try {
      await api.post('/account/delete/', {
        password: deletePassword,
        reason: deleteReason,
      });
      
      // Refresh deletion status
      const deletionRes = await api.get('/account/delete/');
      setDeletionStatus(deletionRes.data);
      setDeleteDialogOpen(false);
      
      // Clear form
      setDeletePassword('');
      setDeleteReason('');
      setDeleteConfirmText('');
      
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to process deletion request');
    } finally {
      setIsDeleting(false);
    }
  };

  // Cancel deletion request
  const handleCancelDeletion = async () => {
    try {
      await api.delete('/account/delete/');
      const deletionRes = await api.get('/account/delete/');
      setDeletionStatus(deletionRes.data);
    } catch (err: any) {
      setError('Failed to cancel deletion request');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
      >
        Back
      </Button>

      <Paper sx={{ p: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Your Data
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View, export, or delete your personal information
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Pending Deletion Warning */}
        {deletionStatus?.has_pending_request && (
          <Alert 
            severity="warning" 
            sx={{ mb: 3 }}
            action={
              deletionStatus.can_cancel && (
                <Button color="inherit" size="small" onClick={handleCancelDeletion}>
                  Cancel Deletion
                </Button>
              )
            }
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Account Deletion Scheduled
            </Typography>
            <Typography variant="body2">
              Your account will be permanently deleted on{' '}
              {formatDate(deletionStatus.scheduled_deletion_at || '')}.
              {deletionStatus.days_remaining !== undefined && (
                <> ({deletionStatus.days_remaining} days remaining)</>
              )}
            </Typography>
          </Alert>
        )}

        <Divider sx={{ mb: 3 }} />

        {/* Personal Information */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon color="primary" />
              <Typography variant="h6">Personal Information</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {userData && (
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: '40%' }}>Email</TableCell>
                      <TableCell>{userData.user.email}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                      <TableCell>{userData.user.first_name} {userData.user.last_name}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Username</TableCell>
                      <TableCell>{userData.user.username}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Account Created</TableCell>
                      <TableCell>{formatDate(userData.user.date_joined)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Last Login</TableCell>
                      <TableCell>{formatDate(userData.user.last_login)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Account Status</TableCell>
                      <TableCell>
                        <Chip 
                          label={userData.user.is_active ? 'Active' : 'Inactive'} 
                          color={userData.user.is_active ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Company Memberships */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon color="primary" />
              <Typography variant="h6">
                Company Memberships ({userData?.companies.length || 0})
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {userData?.companies.length ? (
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {userData.companies.map((company) => (
                      <TableRow key={company.id}>
                        <TableCell sx={{ fontWeight: 600 }}>{company.name}</TableCell>
                        <TableCell>
                          <Chip 
                            label={company.type} 
                            size="small" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={company.role.replace('_', ' ')} 
                            size="small" 
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{formatDate(company.joined_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary">No company memberships</Typography>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Activity Statistics */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssessmentIcon color="primary" />
              <Typography variant="h6">Activity Statistics</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {userData && (
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" color="primary">
                    {userData.statistics.audits_created}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Audits Created
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" color="primary">
                    {userData.statistics.action_items_assigned}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Action Items Assigned
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" color="primary">
                    {userData.statistics.companies_member_of}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Companies
                  </Typography>
                </Box>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Recent Activity */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon color="primary" />
              <Typography variant="h6">Recent Activity</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {userData?.recent_activity.length ? (
              <List dense>
                {userData.recent_activity.slice(0, 10).map((activity, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <CheckIcon color="action" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={activity.action}
                      secondary={`${activity.resource} • ${formatDate(activity.date)}`}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography color="text.secondary">No recent activity</Typography>
            )}
          </AccordionDetails>
        </Accordion>
      </Paper>

      {/* Data Export Section */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <DownloadIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Download Your Data
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Get a copy of all your personal data in JSON format
            </Typography>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Your export will include: profile information, company memberships, audit sessions, 
          action items, comments, templates you've created, and activity history.
        </Typography>

        {exportMessage && (
          <Alert 
            severity={exportMessage.includes('Failed') || exportMessage.includes('only') ? 'warning' : 'success'} 
            sx={{ mb: 2 }}
          >
            {exportMessage}
          </Alert>
        )}

        {/* Recent Exports */}
        {exportStatus?.exports && exportStatus.exports.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Recent Exports:</Typography>
            {exportStatus.exports.slice(0, 3).map((exp) => (
              <Box key={exp.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip
                  label={`${exp.status} - ${formatDate(exp.created_at)}`}
                  size="small"
                  color={exp.status === 'completed' ? 'success' : exp.status === 'pending' ? 'warning' : 'default'}
                />
                {exp.status === 'completed' && exp.is_downloadable && (
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<DownloadIcon />}
                    href={`http://localhost:8001/api/account/export/download/${exp.id}/`}
                    target="_blank"
                  >
                    Download
                  </Button>
                )}
              </Box>
            ))}
          </Box>
        )}

        <Button
          variant="outlined"
          startIcon={isExporting ? <CircularProgress size={20} /> : <DownloadIcon />}
          onClick={handleRequestExport}
          disabled={isExporting}
        >
          {isExporting ? 'Starting Export...' : 'Request Data Export'}
        </Button>
      </Paper>

      {/* Account Deletion Section */}
      <Paper sx={{ p: 4, border: '1px solid', borderColor: 'error.light' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <DeleteIcon sx={{ fontSize: 32, color: 'error.main' }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'error.main' }}>
              Delete Account
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Permanently delete your account and all associated data
            </Typography>
          </Box>
        </Box>

        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Warning:</strong> This action cannot be undone. Your account will be deactivated 
            immediately and permanently deleted after a 30-day grace period. During this time, you 
            can cancel the deletion request.
          </Typography>
        </Alert>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          What will be deleted:
        </Typography>
        <List dense sx={{ mb: 2 }}>
          <ListItem>
            <ListItemIcon><WarningIcon color="error" fontSize="small" /></ListItemIcon>
            <ListItemText primary="Your profile and account information" />
          </ListItem>
          <ListItem>
            <ListItemIcon><WarningIcon color="error" fontSize="small" /></ListItemIcon>
            <ListItemText primary="Your company memberships and permissions" />
          </ListItem>
          <ListItem>
            <ListItemIcon><WarningIcon color="error" fontSize="small" /></ListItemIcon>
            <ListItemText primary="Your activity history and consent records" />
          </ListItem>
        </List>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          What will be preserved (anonymized):
        </Typography>
        <List dense sx={{ mb: 3 }}>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText primary="Review sessions you created (for company records)" />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
            <ListItemText primary="Comments you made (author removed)" />
          </ListItem>
        </List>

        {deletionStatus?.has_pending_request ? (
          <Alert severity="info">
            Deletion already scheduled for {formatDate(deletionStatus.scheduled_deletion_at || '')}
          </Alert>
        ) : (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialogOpen(true)}
          >
            Delete My Account
          </Button>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          Confirm Account Deletion
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 3 }}>
            This action will schedule your account for permanent deletion. You have 30 days to 
            cancel this request before your data is permanently removed.
          </Alert>

          <TextField
            label="Reason for leaving (optional)"
            multiline
            rows={3}
            fullWidth
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            sx={{ mb: 3 }}
            placeholder="Help us improve by sharing why you're leaving..."
          />

          <TextField
            label="Enter your password"
            type="password"
            fullWidth
            required
            value={deletePassword}
            onChange={(e) => setDeletePassword(e.target.value)}
            sx={{ mb: 3 }}
          />

          <TextField
            label='Type "DELETE MY ACCOUNT" to confirm'
            fullWidth
            required
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            error={deleteConfirmText.length > 0 && deleteConfirmText !== 'DELETE MY ACCOUNT'}
            helperText="This confirms you understand this action is irreversible"
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRequestDeletion}
            disabled={isDeleting || deleteConfirmText !== 'DELETE MY ACCOUNT' || !deletePassword}
            startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {isDeleting ? 'Processing...' : 'Delete My Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MyData;

